import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import employeeService from '../services/employeeService';
import apiClient from '../services/api';

const EmployeesPage = () => {
  const [activeTab, setActiveTab] = useState('employees');
  const [employees, setEmployees] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [workAreas, setWorkAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [cacheVersion, setCacheVersion] = useState(1);
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', phone: '', role: '', joining_date: '', department: '', employee_id: '', photo: null, photo_url: '', username: '', password: ''
  });
  const [workAreaFormData, setWorkAreaFormData] = useState({
    employee_id: '', location_name: '', latitude: '', longitude: '', radius_meters: 100, address: ''
  });
  const [showWorkAreaModal, setShowWorkAreaModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && showModal) {
        setShowModal(false);
      }
    };

    window.addEventListener('keydown', handleEscapeKey);
    return () => window.removeEventListener('keydown', handleEscapeKey);
  }, [showModal]);

  const loadData = async () => {
    try {
      setLoading(true);
      if (activeTab === 'employees') {
        const response = await employeeService.getEmployees();
        setEmployees(response.data?.data || []);
      } else if (activeTab === 'leaves') {
        const response = await employeeService.getLeaves();
        setLeaves(response.data?.data || []);
      } else if (activeTab === 'workAreas') {
        const response = await employeeService.getEmployees();
        setEmployees(response.data?.data || []);
        
        // Fetch work areas for all employees
        await loadWorkAreas(response.data?.data || []);
      }
      setError(null);
      // Increment cache version to bust image cache
      setCacheVersion(prev => prev + 1);
    } catch (err) {
      console.error('Error loading data:', err);
      console.error('Error details:', err.response?.data);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to load data';
      setError(`Failed to load data: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const loadWorkAreas = async (employeesList) => {
    try {
      const workAreaMap = {};
      
      // Fetch work area for each employee
      for (const emp of employeesList) {
        try {
          const response = await apiClient.get(`/attendance/admin/work-area/${emp.id}`);
          if (response.data.success) {
            workAreaMap[emp.id] = response.data.work_area;
            console.log(`Work area found for employee ${emp.id}:`, response.data.work_area);
          } else {
            workAreaMap[emp.id] = null;
          }
        } catch (err) {
          console.log(`No work area found for employee ${emp.id}`);
          workAreaMap[emp.id] = null;
        }
      }
      
      setWorkAreas(workAreaMap);
      console.log('All work areas loaded:', workAreaMap);
    } catch (err) {
      console.error('Error loading work areas:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file') {
      setFormData(prev => ({ ...prev, [name]: files[0] || null }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const openAddModal = () => {
    setFormData({ first_name: '', last_name: '', email: '', phone: '', role: '', joining_date: '', department: '', employee_id: '', photo: null, photo_url: '', username: '', password: '' });
    setShowPassword(false);
    setModalMode('add');
    setShowModal(true);
  };

  const openEditModal = (employee) => {
    setSelectedEmployee(employee);
    setFormData({
      first_name: employee.user?.first_name || '', last_name: employee.user?.last_name || '',
      email: employee.user?.email || '', phone: employee.user?.phone || '',
      role: employee.designation || '', joining_date: employee.joining_date || '', department: employee.department || '',
      employee_id: employee.employee_id || '', photo: null, photo_url: employee.user?.profile_photo_url || '', username: employee.user?.username || '', password: ''
    });
    setShowPassword(false);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { photo, photo_url, ...baseData } = formData;
      let submitData = baseData;

      // If there's a photo file, convert to base64
      if (photo) {
        const reader = new FileReader();
        reader.onload = async () => {
          submitData.photo = reader.result; // base64 encoded photo
          
          try {
            if (modalMode === 'add') await employeeService.createEmployee(submitData);
            else await employeeService.updateEmployee(selectedEmployee.id, submitData);
            setShowModal(false);
            loadData();
          } catch (err) {
            console.error('Save error:', err);
            console.error('Error response:', err.response?.data);
            const errorMsg = err.response?.data?.message || err.message || 'Failed to save employee';
            alert(errorMsg);
          }
        };
        reader.readAsDataURL(photo);
        return;
      }

      if (modalMode === 'add') await employeeService.createEmployee(submitData);
      else await employeeService.updateEmployee(selectedEmployee.id, submitData);
      setShowModal(false);
      loadData();
    } catch (err) {
      console.error('Save error:', err);
      console.error('Error response:', err.response?.data);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to save employee';
      alert(errorMsg);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this employee?')) {
      try {
        await employeeService.deleteEmployee(id);
        loadData();
      } catch (err) {
        alert('Failed to delete employee');
      }
    }
  };

  const handleLeaveAction = async (id, action) => {
    try {
      await employeeService.updateLeave(id, { status: action });
      loadData();
    } catch (err) {
      alert('Failed to update leave status');
    }
  };

  const handleWorkAreaInputChange = (e) => {
    const { name, value } = e.target;
    setWorkAreaFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    alert('Requesting location... Please allow location access in your browser');
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setWorkAreaFormData(prev => ({
          ...prev,
          latitude: latitude.toFixed(6),
          longitude: longitude.toFixed(6)
        }));
        alert(`✅ Location captured!\nLatitude: ${latitude.toFixed(6)}\nLongitude: ${longitude.toFixed(6)}`);
      },
      (error) => {
        let errorMsg = 'Failed to get location';
        if (error.code === 1) {
          errorMsg = 'Location permission denied. Please enable location access in your browser settings.';
        } else if (error.code === 2) {
          errorMsg = 'Location information is unavailable. Please try again.';
        } else if (error.code === 3) {
          errorMsg = 'Location request timeout. Please try again.';
        }
        alert(`❌ Error: ${errorMsg}`);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSaveWorkArea = async (e) => {
    e.preventDefault();
    try {
      if (!workAreaFormData.employee_id || !workAreaFormData.location_name || 
          !workAreaFormData.latitude || !workAreaFormData.longitude) {
        alert('Please fill all required fields');
        return;
      }

      const payload = {
        employee_id: parseInt(workAreaFormData.employee_id),
        location_name: workAreaFormData.location_name,
        latitude: parseFloat(workAreaFormData.latitude),
        longitude: parseFloat(workAreaFormData.longitude),
        radius_meters: parseFloat(workAreaFormData.radius_meters) || 100,
        address: workAreaFormData.address
      };

      console.log('Sending work area payload:', payload);
      const response = await apiClient.post('/attendance/admin/work-area/set', payload);
      console.log('Work area response:', response.data);

      if (response.data.success) {
        alert('✅ Work area assigned successfully!\n\nLocation: ' + workAreaFormData.location_name + '\nCoordinates: (' + workAreaFormData.latitude + ', ' + workAreaFormData.longitude + ')\nRadius: ' + workAreaFormData.radius_meters + 'm');
        setWorkAreaFormData({ employee_id: '', location_name: '', latitude: '', longitude: '', radius_meters: 100, address: '' });
        setShowWorkAreaModal(false);
        loadData();
      } else {
        // Handle case when API returns success: false
        const errorMsg = response.data.message || 'Failed to assign work area';
        console.error('API returned success: false -', errorMsg);
        alert('❌ Error: ' + errorMsg + '\n\nPlease check:\n1. Employee ID is correct\n2. Latitude/Longitude are valid\n3. All required fields are filled');
      }
    } catch (err) {
      console.error('Error saving work area:', err);
      console.error('Error response:', err.response?.data);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to save work area';
      alert('❌ Error: ' + errorMsg + '\n\nDebug Info:\n' + JSON.stringify(err.response?.data || err.message));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount || 0);
  };

  const getCacheBustedImageUrl = (imageUrl) => {
    if (!imageUrl) return imageUrl;
    
    let finalUrl = imageUrl;
    
    // If URL doesn't start with http and is relative, add the backend server URL
    if (!imageUrl.startsWith('http')) {
      const apiBase = apiClient.defaults.baseURL; // 'http://localhost:5000/api'
      const serverUrl = apiBase.replace('/api', ''); // 'http://localhost:5000'
      finalUrl = `${serverUrl}${imageUrl}`;
    }
    
    // Add cache-buster query parameter
    const separator = finalUrl.includes('?') ? '&' : '?';
    return `${finalUrl}${separator}v=${cacheVersion}`;
  };

  return (
    <Layout title="Employees">
      <style>{`
        .page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; }
        .tabs { display: flex; gap: 8px; }
        .tab { padding: 10px 20px; border: 1px solid rgba(0, 0, 0, 0.04); background: white; font-size: 14px; font-weight: 500; color: #6b7280; cursor: pointer; border-radius: 12px; box-shadow: 0 4px 8px -2px rgba(99, 102, 241, 0.1), 0 6px 12px -4px rgba(0, 0, 0, 0.05); transition: all 0.2s ease; }
        .tab:hover { background: #f3f4f6; transform: translateY(-1px); box-shadow: 0 6px 12px -2px rgba(99, 102, 241, 0.15), 0 10px 16px -5px rgba(0, 0, 0, 0.06); }
        .tab.active { background: #f7fee7; color: #65a30d; box-shadow: 0 6px 12px -2px rgba(132, 204, 22, 0.2), 0 10px 20px -5px rgba(0, 0, 0, 0.06); }
        .btn { padding: 10px 16px; border: none; border-radius: 8px; cursor: pointer; font-weight: 500; font-size: 14px; }
        .btn-primary { background: #84cc16; color: white; }
        .btn-primary:hover { background: #65a30d; }
        .btn-secondary { background: #f3f4f6; color: #374151; border: 1px solid #d1d5db; }
        .btn-danger { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
        .btn-success { background: #d1fae5; color: #047857; }
        .btn-sm { padding: 6px 12px; font-size: 13px; }
        .stats-row { display: flex; gap: 20px; margin-bottom: 24px; }
        .stat-card { background: white; padding: 20px; border-radius: 16px; border: 1px solid rgba(0, 0, 0, 0.04); flex: 1; box-shadow: 0 6px 12px -2px rgba(139, 92, 246, 0.15), 0 10px 20px -5px rgba(0, 0, 0, 0.05); transition: all 0.2s ease; }
        .stat-card:hover { transform: translateY(-2px); box-shadow: 0 10px 20px -4px rgba(139, 92, 246, 0.2), 0 15px 25px -8px rgba(0, 0, 0, 0.08); }
        .stat-card h3 { font-size: 12px; color: #6b7280; margin: 0 0 8px; text-transform: uppercase; }
        .stat-card .value { font-size: 28px; font-weight: 600; color: #1f2937; }
        .data-table { width: 100%; border-collapse: collapse; }
        .data-table th { background: #f9fafb; padding: 12px 16px; text-align: left; font-size: 12px; font-weight: 600; text-transform: uppercase; color: #6b7280; border-bottom: 1px solid #e5e7eb; }
        .data-table td { padding: 14px 16px; border-bottom: 1px solid #f3f4f6; color: #374151; font-size: 14px; }
        .data-table tr:hover { background: #f9fafb; }
        .status-badge { padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: 500; }
        .status-active { background: #d1fae5; color: #047857; }
        .status-pending { background: #fef3c7; color: #b45309; }
        .status-approved { background: #dbeafe; color: #1d4ed8; }
        .status-rejected { background: #fee2e2; color: #dc2626; }
        .actions { display: flex; gap: 8px; }
        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
        .modal { background: white; border-radius: 16px; width: 100%; max-width: 520px; box-shadow: 0 20px 40px -10px rgba(99, 102, 241, 0.25), 0 30px 50px -15px rgba(0, 0, 0, 0.15); border: 1px solid rgba(0, 0, 0, 0.04); }
        .modal-header { padding: 20px 24px; border-bottom: 1px solid #e5e7eb; display: flex; justify-content: space-between; align-items: center; }
        .modal-header h2 { margin: 0; font-size: 18px; font-weight: 600; }
        .modal-close { background: none; border: none; font-size: 24px; cursor: pointer; color: #6b7280; }
        .modal-body { padding: 24px; }
        .modal-footer { padding: 16px 24px; border-top: 1px solid #e5e7eb; display: flex; justify-content: flex-end; gap: 12px; }
        .form-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
        .form-group { display: flex; flex-direction: column; }
        .form-group label { margin-bottom: 6px; font-weight: 500; color: #374151; font-size: 13px; }
        .form-group input, .form-group select { padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 14px; }
        .password-field-wrapper { position: relative; display: flex; align-items: center; }
        .password-field-wrapper input { flex: 1; padding-right: 40px; }
        .password-toggle-btn { position: absolute; right: 10px; background: none; border: none; cursor: pointer; color: #6b7280; font-size: 18px; padding: 4px 6px; display: flex; align-items: center; justify-content: center; }
        .password-toggle-btn:hover { color: #374151; }
        .form-group input:focus, .form-group select:focus { outline: none; border-color: #84cc16; }
        .loading, .error, .empty { text-align: center; padding: 48px; color: #6b7280; }
        .error { color: #dc2626; }
      `}</style>

      <div className="page-header">
        <div className="tabs">
          <button className={`tab ${activeTab === 'employees' ? 'active' : ''}`} onClick={() => setActiveTab('employees')}>Employees</button>
          <button className={`tab ${activeTab === 'leaves' ? 'active' : ''}`} onClick={() => setActiveTab('leaves')}>Leave Requests</button>
          <button className={`tab ${activeTab === 'workAreas' ? 'active' : ''}`} onClick={() => setActiveTab('workAreas')}>Work Areas</button>
        </div>
        {activeTab === 'employees' && <button className="btn btn-primary" onClick={openAddModal}>+ Add Employee</button>}
        {activeTab === 'workAreas' && <button className="btn btn-primary" onClick={() => setShowWorkAreaModal(true)}>+ Assign Work Area</button>}
      </div>

      <div className="stats-row">
        <div className="stat-card">
          <h3>Total Employees</h3>
          <div className="value">{employees.length}</div>
        </div>
        <div className="stat-card">
          <h3>Pending Leaves</h3>
          <div className="value">{leaves.filter(l => l.status === 'pending').length}</div>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : activeTab === 'employees' ? (
        <table className="data-table">
          <thead>
            <tr><th>Photo</th><th>Employee ID</th><th>Name</th><th>Email</th><th>Phone</th><th>Role</th><th>Department</th><th>Joined</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {employees.length === 0 ? (
              <tr><td colSpan="9" style={{ textAlign: 'center' }}>No employees found</td></tr>
            ) : employees.map((emp) => (
              <tr key={emp.id}>
                <td style={{ textAlign: 'center' }}>
                  {emp.user?.profile_photo_url ? (
                    <img src={getCacheBustedImageUrl(emp.user.profile_photo_url)} alt={`${emp.user?.first_name} ${emp.user?.last_name}`} style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', fontWeight: 'bold' }}>
                      {emp.user?.first_name?.charAt(0)}{emp.user?.last_name?.charAt(0)}
                    </div>
                  )}
                </td>
                <td><small style={{ color: '#6b7280' }}>{emp.employee_id || 'N/A'}</small></td>
                <td><strong>{emp.user?.first_name} {emp.user?.last_name}</strong></td>
                <td>{emp.user?.email}</td>
                <td>{emp.user?.phone}</td>
                <td>{emp.designation}</td>
                <td>{emp.department || 'N/A'}</td>
                <td>{formatDate(emp.joining_date)}</td>
                <td>
                  <div className="actions">
                    <button className="btn btn-secondary btn-sm" onClick={() => openEditModal(emp)}>Edit</button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(emp.id)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : activeTab === 'leaves' ? (
        <table className="data-table">
          <thead>
            <tr><th>Employee</th><th>From</th><th>To</th><th>Reason</th><th>Status</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {leaves.length === 0 ? (
              <tr><td colSpan="6" style={{ textAlign: 'center' }}>No leave requests found</td></tr>
            ) : leaves.map((leave) => (
              <tr key={leave.id}>
                <td><strong>{leave.employee?.user?.first_name} {leave.employee?.user?.last_name}</strong></td>
                <td>{formatDate(leave.start_date)}</td>
                <td>{formatDate(leave.end_date)}</td>
                <td>{leave.reason || 'N/A'}</td>
                <td><span className={`status-badge status-${leave.status}`}>{leave.status}</span></td>
                <td>
                  {leave.status === 'pending' && (
                    <div className="actions">
                      <button className="btn btn-success btn-sm" onClick={() => handleLeaveAction(leave.id, 'approved')}>Approve</button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleLeaveAction(leave.id, 'rejected')}>Reject</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : activeTab === 'workAreas' ? (
        <table className="data-table">
          <thead>
            <tr><th>Employee</th><th>Location</th><th>Coordinates</th><th>Radius</th><th>Address</th><th>Status</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {employees.length === 0 ? (
              <tr><td colSpan="7" style={{ textAlign: 'center' }}>No employees found</td></tr>
            ) : employees.map((emp) => {
              const wa = workAreas[emp.id];
              return (
                <tr key={emp.id}>
                  <td><strong>{emp.user?.first_name} {emp.user?.last_name}</strong><br/><small style={{ color: '#6b7280' }}>{emp.employee_id}</small></td>
                  <td>{wa ? <strong>{wa.location_name}</strong> : <span style={{padding: '4px 8px', background: '#fef2f2', color: '#dc2626', borderRadius: '4px', fontSize: '12px' }}>Not assigned</span>}</td>
                  <td>{wa ? `(${wa.latitude.toFixed(6)}, ${wa.longitude.toFixed(6)})` : '-'}</td>
                  <td>{wa ? `${wa.radius_meters}m` : '-'}</td>
                  <td>{wa ? wa.address : '-'}</td>
                  <td>
                    {wa ? (
                      <span style={{padding: '4px 8px', background: '#d1fae5', color: '#047857', borderRadius: '4px', fontSize: '12px', fontWeight: '500' }}>✅ Assigned</span>
                    ) : (
                      <span style={{padding: '4px 8px', background: '#fef3c7', color: '#b45309', borderRadius: '4px', fontSize: '12px', fontWeight: '500' }}>⏳ Pending</span>
                    )}
                  </td>
                  <td>
                    <button className="btn btn-primary btn-sm" onClick={() => { setWorkAreaFormData({...workAreaFormData, employee_id: emp.id.toString(), ...(wa || {})}); setShowWorkAreaModal(true); }}>
                      {wa ? 'Edit' : 'Assign'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <div className="empty">No data</div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{modalMode === 'add' ? 'Add Employee' : 'Edit Employee'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                <div className="form-grid">
                  <div className="form-group"><label>Employee ID *</label><input type="text" name="employee_id" value={formData.employee_id} onChange={handleInputChange} required /></div>
                  <div className="form-group"><label>First Name *</label><input type="text" name="first_name" value={formData.first_name} onChange={handleInputChange} required /></div>
                  <div className="form-group"><label>Last Name *</label><input type="text" name="last_name" value={formData.last_name} onChange={handleInputChange} required /></div>
                  <div className="form-group"><label>Email *</label><input type="email" name="email" value={formData.email} onChange={handleInputChange} required /></div>
                  <div className="form-group"><label>Username *</label><input type="text" name="username" value={formData.username} onChange={handleInputChange} required /></div>
                  <div className="form-group">
                    <label>Password {modalMode === 'add' ? '*' : '(leave blank to keep current)'}</label>
                    <div className="password-field-wrapper">
                      <input 
                        type={showPassword ? 'text' : 'password'} 
                        name="password" 
                        value={formData.password} 
                        onChange={handleInputChange} 
                        required={modalMode === 'add'} 
                      />
                      <button 
                        type="button" 
                        className="password-toggle-btn" 
                        onClick={() => setShowPassword(!showPassword)}
                        title={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? '👁️' : '👁️‍🗨️'}
                      </button>
                    </div>
                  </div>
                  <div className="form-group"><label>Phone *</label><input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} required /></div>
                  <div className="form-group"><label>Role *</label><input type="text" name="role" value={formData.role} onChange={handleInputChange} required /></div>
                  <div className="form-group"><label>Department</label><input type="text" name="department" value={formData.department} onChange={handleInputChange} /></div>
                  <div className="form-group"><label>Joining Date</label><input type="date" name="joining_date" value={formData.joining_date} onChange={handleInputChange} /></div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Photo</label>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                      <input type="file" name="photo" onChange={handleInputChange} accept="image/*" style={{ flex: 1 }} />
                      {(formData.photo_url || formData.photo) && (
                        <img 
                          src={formData.photo ? URL.createObjectURL(formData.photo) : getCacheBustedImageUrl(formData.photo_url)} 
                          alt="Preview" 
                          style={{ width: '60px', height: '60px', borderRadius: '8px', objectFit: 'cover', flexShrink: 0 }} 
                        />
                      )}
                    </div>
                  </div>
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSubmit}>{modalMode === 'add' ? 'Add Employee' : 'Save Changes'}</button>
            </div>
          </div>
        </div>
      )}

      {showWorkAreaModal && (
        <div className="modal-overlay" onClick={() => setShowWorkAreaModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Assign Work Area</h2>
              <button className="modal-close" onClick={() => setShowWorkAreaModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSaveWorkArea}>
                <div className="form-grid">
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Select Employee *</label>
                    <select name="employee_id" value={workAreaFormData.employee_id} onChange={handleWorkAreaInputChange} required>
                      <option value="">-- Select Employee --</option>
                      {employees.map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {emp.user?.first_name} {emp.user?.last_name} ({emp.employee_id})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Location Name *</label>
                    <input type="text" name="location_name" placeholder="e.g., Main Office" value={workAreaFormData.location_name} onChange={handleWorkAreaInputChange} required />
                  </div>
                  <div className="form-group">
                    <label>Latitude *</label>
                    <input type="number" name="latitude" placeholder="e.g., 19.9641" step="0.0001" value={workAreaFormData.latitude} onChange={handleWorkAreaInputChange} required />
                  </div>
                  <div className="form-group">
                    <label>Longitude *</label>
                    <input type="number" name="longitude" placeholder="e.g., 73.7836" step="0.0001" value={workAreaFormData.longitude} onChange={handleWorkAreaInputChange} required />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <button 
                      type="button" 
                      onClick={handleUseCurrentLocation}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        background: '#0ea5e9',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        fontSize: '14px',
                        transition: 'background 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#0284c7'}
                      onMouseLeave={(e) => e.target.style.background = '#0ea5e9'}
                    >
                      📍 Use Current Location
                    </button>
                  </div>
                  <div className="form-group">
                    <label>Radius (meters)</label>
                    <input type="number" name="radius_meters" placeholder="100" value={workAreaFormData.radius_meters} onChange={handleWorkAreaInputChange} min="10" step="10" />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Address</label>
                    <input type="text" name="address" placeholder="Full address for reference" value={workAreaFormData.address} onChange={handleWorkAreaInputChange} />
                  </div>
                  <div style={{ gridColumn: '1 / -1', fontSize: '12px', color: '#6b7280', background: '#f0f9ff', padding: '12px', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                    <strong>How to find GPS coordinates:</strong><br/>
                    1. Open Google Maps<br/>
                    2. Search for your office location<br/>
                    3. Right-click on the location<br/>
                    4. Copy the coordinates shown (Latitude, Longitude)<br/>
                    5. Paste them above
                  </div>
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowWorkAreaModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveWorkArea}>Assign Work Area</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default EmployeesPage;
